import React from 'react';
import { GlobalState, UI } from '@leverege/ui-redux';
import I18N from '@leverege/i18n';
import { UserRoles, ItemDelegate, Relationship } from '@leverege/molten-core';
import { LinkAction } from "../routes/index.js";
import UserManagementScreen from "../screens/userManagement/LazyUserManagementScreen.js";
import Util from "./Util.js";
import RelationshipBreadcrumbs from "./view/RelationshipBreadcrumbs.js";
const {
  t,
  tf,
  tfIcon
} = I18N.ns('molten.imagine.UserAccess');

/**
 * Processes the relationship for user management delegate. If the relationship blueprint has
 * the `has:userManagement` tag, a `People` link will be added to the nav bar leading to the 
 * resource users screen.
 */
function processRelationship(molten, relationship, options) {
  // Get parameters
  const {
    blueprint,
    attribute,
    path
  } = relationship;

  // Check if blueprint has user management
  if (!blueprint.hasTag('has:userManagement')) {
    return;
  }

  // Initialize parameters
  const actionPath = `${relationship.urlPath}/${relationship.isGroup ? `:${blueprint.type}Id/` : ''}access`;
  const excludeFrom = blueprint.getTagsStartingWith('excludeUserManagementFrom:', true);
  const attrExcludeFrom = attribute ? attribute.getTagsStartingWith('excludeUserManagementFrom:', true) : [];
  const navSortUsers = blueprint.getTagStartingWith('navSortUserManagement:', true) || 'zzz';
  const sectionName = blueprint.getString('userManagementSectionName');

  // Add link
  molten.addPlugin('Action', LinkAction.create({
    id: `link.${path}.ResourceUsers`,
    matches: {
      client: 'ItemScreen',
      objectType: blueprint.type,
      path,
      use: 'navBar'
    },
    name: tf('name'),
    icon: tfIcon('icon'),
    path: actionPath,
    layout: {
      sort: navSortUsers,
      // put at end
      sectionName
    },
    handles: cxt => {
      const aPersona = UI.get(GlobalState.get()).activePersona?.name;
      if (Util.shouldExclude(aPersona, attribute, excludeFrom, attrExcludeFrom)) {
        return false;
      }
      return true;
    }
  }));

  // Add Breadcrumbs
  molten.addPlugin('Route', {
    id: `route.${path}.ResourceUsers:breadcrumbs`,
    matches: {
      client: 'Breadcrumbs'
    },
    exact: true,
    path: actionPath,
    render: ({
      match,
      ...rest
    }) => {
      const rel = relationship.createItem(match);
      const name = t('name');
      const extraCrumbs = [{
        name,
        tooltip: name,
        url: actionPath
      }];
      return /*#__PURE__*/React.createElement(RelationshipBreadcrumbs, {
        match,
        ...rest,
        extraCrumbs,
        relationship: rel
      });
    }
  });
  // Add route
  molten.addPlugin('Route', {
    // id : `${blueprint.type}.route.ResourceUsers`,
    id: `route.${path}.ResourceUsers`,
    exact: true,
    path: actionPath,
    matches: {
      client: 'ItemScreen',
      path,
      objectType: blueprint.type
    },
    render: ({
      match,
      location,
      history,
      ...rest
    }) => {
      const iRel = relationship.createItem(match);
      const allUserRoles = UI.get(GlobalState.get(), 'allUserRoles');

      // Create the role filter
      const roleFilter = UserRoles.createRoleFilter(allUserRoles, Relationship.getResourceIdHierarchy(relationship, match), blueprint, molten);
      const delegate = new ItemDelegate({
        match,
        relationship: iRel,
        id: iRel.id
      });
      const props = {
        ...rest,
        match,
        location,
        history,
        relationship,
        title: t('title'),
        selectorProps: {
          simple: true,
          roleFilter
        },
        variants: {
          editorPane: 'formBody',
          inputLabel: 'inputTitle',
          inputText: 'default',
          inputCaption: 'caption',
          selectionSubtitle: 'caption',
          titleBar: 'screenTitle',
          toolbar: 'actionBar'
        },
        projectId: molten.getConfig('api/projectId'),
        resourceId: iRel.id,
        systemId: relationship?.systemId,
        delegate,
        // TODO ITEM ACTIONS
        actions: delegate,
        // TODO: Remove in future version
        emailOptions: molten.getConfig('userManagement/emailOptions'),
        smsOptions: molten.getConfig('userManagement/smsOptions'),
        allowSmsInvitation: molten.getConfig('userManagement/allowSmsInvitation'),
        uiName: molten.getConfig('uiName')
        // delegate : Interface.create( relationship.systemId, blueprint.type ).obj( resourceId )
      };
      return /*#__PURE__*/React.createElement(UserManagementScreen, props);
    }
  });
}
export default {
  processRelationship
};